import config from "appConfig";

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>
   The previous lesson was devoted to the presentation of the impulse response of a LTI system that was qualified as its signature. This lesson presents how it can be used to synthesize the response
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>y</mi>
   </math>
   of the system
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold-script>S</mi>
   </math>
   to a given input
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   and not just a Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   .<br class=newline>
<p>
   First, the input signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   can be written as a continuous superposition of delayed Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   weighted by the amplitude of the signal:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>≡</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>d</mtext>
            </mstyle>
            <mi>τ</mi>
         </math>
      <td class=eq-no>(1)
</table>
<p>By superposition and linearity of the integral, the response of the system is then:
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant=bold-script>S</mi>
            <mo fence=true form=prefix>[</mo>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo fence=true form=prefix>]</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=bold-script>S</mi>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <msub>
                  <mo>∫</mo>
                  <mi>ℝ</mi>
                  </msub>
                  <mi>x</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>τ</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
                  <mi>δ</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi>τ</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
                  <mstyle class=text>
                     <mtext class=textrm mathvariant=normal>d</mtext>
                  </mstyle>
                  <mi>τ</mi>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi mathvariant=bold-script>S</mi>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <mi>x</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>τ</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
                  <mi>δ</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi>τ</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>d</mtext>
            </mstyle>
            <mi>τ</mi>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   The integrand correponds to the response of the system to a delayed Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   . We have first, by time invariance:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant=bold-script>S</mi>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo fence=true form=prefix>[</mo>
               </mrow>
            </mstyle>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo fence=true form=prefix>]</mo>
               </mrow>
            </mstyle>
            <mo class=MathClass-rel>=</mo>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>Then, by linearity:
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant=bold-script>S</mi>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo fence=true form=prefix>[</mo>
               </mrow>
            </mstyle>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo fence=true form=prefix>]</mo>
               </mrow>
            </mstyle>
            <mo class=MathClass-rel>=</mo>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   The response of the system to an input signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   is then:
<table class=equation>
   <tr>
      <td style="font-size: 15px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant=bold-script>S</mi>
            <mo fence=true form=prefix>[</mo>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo fence=true form=prefix>]</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>d</mtext>
            </mstyle>
            <mi>τ</mi>
         </math>
      <td class=eq-no>(5)
</table>
<p>We then ends with an integral that depends on the input signal and the impulse response of the signal. This operation is called convolution and its general definition is:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>f</mi>
            <mo class=MathClass-bin>∗</mo>
            <mi>g</mi>
            <mi>t</mi>
            <mo class=MathClass-rel>↦</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi>f</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>g</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>d</mi>
            <mi>τ</mi>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   Convolution between two functions is then a third function which represents how the two functions mutually interact. Its definition is quite tricky and you can familiarize yourself with it with the first applet of the principal part.
   <figure class=figure>
   <img alt="Figure 1. The response of a system is the convolution of the input by the impulse response of the system." src=${config.ASSETS_URL}/03/03/theory/figure-1.svg>
   <figcaption class=caption>Figure 1. The response of a system is the convolution of the input by the impulse response of the system.</figcaption></figure>
<h3 class=sectionHead>Examples</h3>
<p>
   The delay system
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant=bold-script>𝒟</mi>
         </mrow>
         <mrow>
            <mi>Δ</mi>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   , whose impulse response is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>t</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>Δ</mi>
      <mi>t</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   was presented in the previous lesson. Its response
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>y</mi>
   </math>
   to an input signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   is:
<table class=equation>
   <tr>
      <td style="font-size: 6px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>y</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>Δ</mi>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>d</mi>
            <mi>τ</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>Δ</mi>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <msup>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>d</mi>
            <msup>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>Δ</mi>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   Convolution then shows that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>y</mi>
   </math>
   is the original signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   with a delay equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Δ</mi>
      <mi>t</mi>
   </math>
   .<br class=newline>
<p>
   Similarly, let’s come back to the example of the reflection on a rigid boundary in a semi-infinite acoustic medium. Let
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
   </math>
   be the temporal signal emitted by the source. The signal at the receiver is
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>y</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi>s</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo fence=true form=prefix>[</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo fence=true form=prefix>]</mo>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>d</mtext>
            </mstyle>
            <mi>τ</mi>
            <mo class=MathClass-rel linebreak='newline' linebreakstyle='after'>=</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mi>s</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mi>s</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>It is then the sum of two copies of the original signal modified in amplitude due to geometric divergence and with a delay associated to the propagation of the incident and reflected waves from the source to the receiver.<br class=newline>
<p>Convolution can also be used to add reverberation to a given acoustic signal. You can test some examples in the second applet of the principle part.
<h3 class=sectionHead>Properties of convolution</h3>
<p>Convolution is <strong><span class=cmbx-10>commutative</span></strong> (or symmetric): if you exchange the two functions, convolution is preserved:
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-bin>∗</mo>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>d</mi>
            <mi>τ</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <msup>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>d</mi>
            <msup>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mi>h</mi>
            <mo class=MathClass-bin>∗</mo>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>Convolution is also <strong><span class=cmbx-10>associative</span></strong>:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-bin>∗</mo>
            <mo fence=true form=prefix>[</mo>
            <mi>h</mi>
            <mo class=MathClass-bin>∗</mo>
            <mi>g</mi>
            <mo fence=true form=prefix>]</mo>
            <mo class=MathClass-rel>=</mo>
            <mo fence=true form=prefix>[</mo>
            <mi>x</mi>
            <mo class=MathClass-bin>∗</mo>
            <mi>h</mi>
            <mo fence=true form=prefix>]</mo>
            <mo class=MathClass-bin>∗</mo>
            <mi>g</mi>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   These two properties have interesting consequences illustrated in <a href=# data-img-alt="Figure 2: Convolution of systems in cascade." data-img-src=${config.ASSETS_URL}/03/03/theory/figure-2.svg>Figure 2</a>. Let
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   be two LTI systems in cascade (output of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   is the input of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   ). The output of the global system is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>[</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>∗</mo>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-close>]</mo>
      <mo class=MathClass-bin>∗</mo>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   . By associativity, it is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-bin>∗</mo>
      <mo class=MathClass-open>[</mo>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∗</mo>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-close>]</mo>
   </math>
   . Hence the impulse response of the global system is the convolution of the impulse responses of the two systems. In addition, due to commutativity, the impulse response is also
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∗</mo>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   which corresponds to the cascade of two systems but in reverse order.<br class=newline>
   <figure class=figure>
   <img alt="Figure 2. Convolution of systems in cascade." src=${config.ASSETS_URL}/03/03/theory/figure-2.svg>
   <figcaption class=caption>Figure 2. Convolution of systems in cascade.</figcaption></figure>
<p>Convolution is <strong><span class=cmbx-10>bilinear</span></strong> (i.e. linear with respect to the two functions):
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-bin>∗</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <msub>
               <mrow>
                  <mi>λ</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>λ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>λ</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mi>x</mi>
            <mo class=MathClass-bin>∗</mo>
            <msub>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>λ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mi>x</mi>
            <mo class=MathClass-bin>∗</mo>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   This is a logical consequence of the linearity of the integral. In particular, when
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>λ</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>λ</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   , it said that convolution is distributive with respect to addition. It corresponds to the case of systems in parallel. Linearity with respect to the first element is a consequence of commutativity or can also be seen as consequence of the linearity of the system.<br class=newline>
<p>
   In Lesson 3.2. we have seen that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   is the impulse response of the system that does not modify the signal (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold-script>S</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>x</mi>
   </math>
   ). It can be interpreted that Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   is the identity element of convolution. This can be also obtained formally:
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>δ</mi>
            <mo class=MathClass-bin>∗</mo>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>d</mtext>
            </mstyle>
            <mi>τ</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(12)
</table>
<p>We have also seen that causality implies that the values of the impulse response of a system is zero for negative values of time. This property is now easy to show. We have
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>y</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>d</mtext>
            </mstyle>
            <mi>τ</mi>
         </math>
      <td class=eq-no>(13)
</table>
<p>
   As
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>y</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   should only depend on the past values of the input signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   (which corresponds to values at time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>τ</mi>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>τ</mi>
      <mo class=MathClass-rel>≥</mo>
      <mn>0</mn>
   </math>
   ), it necessary that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>τ</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   for negative values of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>τ</mi>
   </math>
   .
<p>We will also come back on stability. A LTI system is said to be stable if every bounded input produces a bounded output:
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi class=MathClass-op>∀</mi>
            <mo>⁡</mo>
            <mi>t</mi>
            <mrow>
               <mo fence=true form=prefix>|</mo>
               <mrow>
                  <mi>x</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mo fence=true form=postfix>|</mo>
            </mrow>
            <mo class=MathClass-rel><</mo>
            <mi>X</mi>
            <mo class=MathClass-rel>⇒</mo>
            <mi class=MathClass-op>∃</mi>
            <mo>⁡</mo>
            <mi>Y</mi>
            <mi class=MathClass-op>∀</mi>
            <mo>⁡</mo>
            <mi>t</mi>
            <mi mathvariant=bold-script>S</mi>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo fence=true form=prefix>[</mo>
               </mrow>
            </mstyle>
            <mrow>
               <mo fence=true form=prefix>|</mo>
               <mrow>
                  <mi>x</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mo fence=true form=postfix>|</mo>
            </mrow>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo fence=true form=prefix>]</mo>
               </mrow>
            </mstyle>
            <mo class=MathClass-rel><</mo>
            <mi>Y</mi>
         </math>
      <td class=eq-no>(14)
</table>
<p>We have
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>|</mo>
               <mrow>
                  <mi mathvariant=bold-script>S</mi>
                  <mstyle mathsize=1.61em>
                     <mrow>
                        <mo fence=true form=prefix>[</mo>
                     </mrow>
                  </mstyle>
                  <mrow>
                     <mo fence=true form=prefix>|</mo>
                     <mrow>
                        <mi>x</mi>
                        <mo class=MathClass-open stretchy=false>(</mo>
                        <mi>t</mi>
                        <mo class=MathClass-close stretchy=false>)</mo>
                     </mrow>
                     <mo fence=true form=postfix>|</mo>
                  </mrow>
                  <mstyle mathsize=1.61em>
                     <mrow>
                        <mo fence=true form=prefix>]</mo>
                     </mrow>
                  </mstyle>
               </mrow>
               <mo fence=true form=postfix>|</mo>
            </mrow>
            <mo class=MathClass-rel>≤</mo>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mrow>
               <mo fence=true form=prefix>|</mo>
               <mrow>
                  <mi>h</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>τ</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mo fence=true form=postfix>|</mo>
            </mrow>
            <mrow>
               <mo fence=true form=prefix>|</mo>
               <mrow>
                  <mi>x</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi>τ</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mo fence=true form=postfix>|</mo>
            </mrow>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>d</mtext>
            </mstyle>
            <mi>τ</mi>
            <mo class=MathClass-rel>≤</mo>
            <mi>X</mi>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mrow>
               <mo fence=true form=prefix>|</mo>
               <mrow>
                  <mi>h</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>τ</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mo fence=true form=postfix>|</mo>
            </mrow>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>d</mtext>
            </mstyle>
            <mi>τ</mi>
         </math>
      <td class=eq-no>(15)
</table>
<p>and consequently
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
          <msubsup>
            <mo>∫</mo>
            <mn>0</mn>
            <mrow>
            <mo class=MathClass-bin>+</mo>
            <mi>∞</mi>
            </mrow>
            </msubsup>
            <mrow>
               <mo fence=true form=prefix>|</mo>
               <mrow>
                  <mi>h</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mo fence=true form=postfix>|</mo>
            </mrow>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>d</mtext>
            </mstyle>
            <mi>t</mi>
            <mo class=MathClass-rel><</mo>
            <mo class=MathClass-bin>+</mo>
            <mi>∞</mi>
         </math>
      <td class=eq-no>(16)
</table>
<p>In most of the application cases, except for very special cases, convolution is not computed in the time domain but in the frequency domain. We then need a tool to do this transformation. It is the Fourier Transform that is the objective of the next lesson.

<h3 class=sectionHead>Glossary</h3>
<dl>
  <dt>LTI
  <dd>Linear Time-Invariant</dd>
</dl>`;

export default theory;
